import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useState } from "react";
import { BoardSelectBoardListItem } from "~/components/BoardSelect/BoardSelectBoardListItem";
import { BoardSelectLibraryListItem } from "~/components/BoardSelect/BoardSelectLibraryListItem";
import { BoardSelectList } from "~/components/BoardSelect/components/BoardSelectList";
import { BoardSelectListHeader } from "~/components/BoardSelect/components/BoardSelectListHeader";
import { BoardSelectListItem } from "~/components/BoardSelect/components/BoardSelectListItem";
import { BoardSelectSearch } from "~/components/BoardSelect/components/BoardSelectSearch";
import { GeneralLibrary } from "~/utils/librariesUtils";
export const BoardSelect = /*#__PURE__*/ memo((param)=>{
    let { boards, children, getBoardDisabledMessage, getLibraryDisabledMessage, hasLibraries, initialBoard, isBoardDisabled, isBoardSelected, isLibrarySelected, libraries, search, onCreateBoard, onCreateLibrary, onSearchChange, onSelectBoard, onSelectLibrary, shouldShowSubBoards } = param;
    /**
     * We use `useState` instead of `ref` to ensure the scroll element is always up to date.
     */ const [scrollElement, setScrollElement] = useState();
    const [expanded, setExpanded] = useState({
        libraries: (initialBoard === null || initialBoard === void 0 ? void 0 : initialBoard.library) ? new Set([
            initialBoard.library.id
        ]) : new Set([
            GeneralLibrary.id
        ]),
        boards: initialBoard ? new Set([
            initialBoard.id
        ]) : new Set()
    });
    const isBoardExpanded = useCallback((id)=>expanded.boards.has(id), [
        expanded.boards
    ]);
    const isLibraryExpanded = useCallback((library)=>expanded.libraries.has(library.id), [
        expanded.libraries
    ]);
    const onBoardOpenChange = useCallback((param)=>{
        let { id, isOpen } = param;
        setExpanded((prev)=>{
            const { boards } = prev;
            const newBoards = new Set(boards);
            if (isOpen) {
                newBoards.add(id);
            } else {
                newBoards.delete(id);
            }
            return {
                ...prev,
                boards: newBoards
            };
        });
    }, []);
    const onLibraryOpenChange = useCallback((param)=>{
        let { id, isOpen } = param;
        setExpanded((prev)=>{
            const { libraries } = prev;
            const newLibraries = new Set(libraries);
            if (isOpen) {
                newLibraries.add(id);
            } else {
                newLibraries.delete(id);
            }
            return {
                ...prev,
                libraries: newLibraries
            };
        });
    }, []);
    const boardsListEl = scrollElement ? /*#__PURE__*/ _jsx(BoardSelectList, {
        initialIndex: !hasLibraries && initialBoard ? boards.findIndex((b)=>b.id === initialBoard.id) : undefined,
        items: boards,
        onCreate: onCreateBoard,
        renderListItem: (param)=>{
            let { data } = param;
            return /*#__PURE__*/ _jsx(BoardSelectBoardListItem, {
                board: data,
                getBoardDisabledMessage: getBoardDisabledMessage,
                isBoardDisabled: isBoardDisabled,
                isBoardExpanded: isBoardExpanded,
                isBoardSelected: isBoardSelected,
                onSelectBoard: onSelectBoard,
                onBoardOpenChange: onBoardOpenChange,
                shouldShowSubBoards: shouldShowSubBoards
            });
        },
        scrollElement: scrollElement,
        title: "Boards"
    }) : null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex w-full flex-col gap-3",
        children: [
            !!onSearchChange && /*#__PURE__*/ _jsx(BoardSelectSearch, {
                onChange: (event)=>{
                    onSearchChange(event);
                    /**
               * Scrolls to the top of the list when the search changes.
               */ scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollTo(0, 0);
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex h-[320px] flex-col overflow-y-auto rounded border border-grey-5 bg-grey-1 p-2 pt-4",
                ref: (ref)=>setScrollElement(ref),
                children: [
                    /*#__PURE__*/ _jsx(BoardSelectListHeader, {
                        onCreate: onCreateBoard,
                        children: hasLibraries ? "Libraries" : "Boards"
                    }),
                    !!scrollElement && /*#__PURE__*/ _jsx(_Fragment, {
                        children: !hasLibraries ? boardsListEl : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                !!boards.length && /*#__PURE__*/ _jsx(BoardSelectListItem, {
                                    isExpanded: isLibraryExpanded === null || isLibraryExpanded === void 0 ? void 0 : isLibraryExpanded({
                                        id: GeneralLibrary.id
                                    }),
                                    id: "general-library",
                                    onSelect: ()=>onLibraryOpenChange({
                                            id: GeneralLibrary.id,
                                            isOpen: !(isLibraryExpanded === null || isLibraryExpanded === void 0 ? void 0 : isLibraryExpanded({
                                                id: GeneralLibrary.id
                                            }))
                                        }),
                                    onOpenChange: (isOpen)=>onLibraryOpenChange({
                                            id: GeneralLibrary.id,
                                            isOpen
                                        }),
                                    prefix: /*#__PURE__*/ _jsx("div", {
                                        className: "flex size-6 items-center justify-center rounded bg-grey-4",
                                        children: /*#__PURE__*/ _jsx(GeneralLibrary.Icon, {
                                            className: "size-4 text-grey-10"
                                        })
                                    }),
                                    title: "General",
                                    children: boardsListEl
                                }),
                                !!libraries.length && /*#__PURE__*/ _jsx(BoardSelectList, {
                                    initialIndex: (initialBoard === null || initialBoard === void 0 ? void 0 : initialBoard.library) && !search ? libraries.findIndex((l)=>{
                                        var _initialBoard_library;
                                        return l.id === (initialBoard === null || initialBoard === void 0 ? void 0 : (_initialBoard_library = initialBoard.library) === null || _initialBoard_library === void 0 ? void 0 : _initialBoard_library.id);
                                    }) : undefined,
                                    items: libraries,
                                    onCreate: onCreateLibrary,
                                    renderListItem: (param)=>{
                                        let { data } = param;
                                        return /*#__PURE__*/ _jsx(BoardSelectLibraryListItem, {
                                            getBoardDisabledMessage: getBoardDisabledMessage,
                                            getLibraryDisabledMessage: getLibraryDisabledMessage,
                                            isLibraryExpanded: isLibraryExpanded,
                                            library: data,
                                            isBoardDisabled: isBoardDisabled,
                                            isBoardExpanded: isBoardExpanded,
                                            isBoardSelected: isBoardSelected,
                                            isLibrarySelected: isLibrarySelected,
                                            onBoardOpenChange: onBoardOpenChange,
                                            onLibraryOpenChange: onLibraryOpenChange,
                                            onSelectBoard: onSelectBoard,
                                            onSelectLibrary: onSelectLibrary,
                                            search: search,
                                            shouldShowSubBoards: shouldShowSubBoards
                                        });
                                    },
                                    scrollElement: scrollElement,
                                    title: "Libraries"
                                })
                            ]
                        })
                    }),
                    children
                ]
            })
        ]
    });
});
BoardSelect.displayName = "BoardSelect";
